import React, { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { FiRefreshCw, FiPlayCircle } from "react-icons/fi";
import adminApi from "../../services/adminApi";
import LoadingState from "../../components/Admin/LoadingState";
import "../../styles/Admin.css";

const STATUS_BADGE_CLASS = {
  success: "admin-badge admin-badge-success",
  failed: "admin-badge admin-badge-error",
  running: "admin-badge admin-badge-running",
};

const truncate = (value, length = 160) => {
  if (!value) return "-";
  if (value.length <= length) return value;
  return `${value.slice(0, length)}…`;
};

const formatDuration = (ms) => {
  if (ms == null) return "-";
  if (ms < 1000) return `${ms} ms`;
  const seconds = ms / 1000;
  if (seconds < 60) return `${seconds.toFixed(1)} s`;
  const minutes = Math.floor(seconds / 60);
  const remaining = seconds % 60;
  return `${minutes}m ${Math.round(remaining)}s`;
};

const KNOWN_JOBS = [
  {
    label: "Droplet Status Sync",
    value: "droplet_status_sync",
  },
  {
    label: "Droplet Task Status Sync",
    value: "droplet_task_status_sync",
  },
  {
    label: "Renewal Invoice Check",
    value: "renewal_invoice_check",
  },
  {
    label: "Overdue Suspension Check",
    value: "overdue_suspension_check",
  },
  {
    label: "Overdue Termination Check",
    value: "overdue_termination_check",
  },
];

const CronLogs = () => {
  const [data, setData] = useState({ logs: [], jobSummary: {}, totals: {} });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedJob, setSelectedJob] = useState(KNOWN_JOBS[0].value);
  const [invoking, setInvoking] = useState(false);

  const loadLogs = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await adminApi.get("/admin/cron/logs?limit=500");
      setData(res.data || { logs: [], jobSummary: {}, totals: {} });
    } catch (err) {
      const message =
        err.response?.data?.message || "Failed to load cron job logs";
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const invokeCron = async () => {
    setInvoking(true);
    try {
      const response = await adminApi.post("/admin/cron/run", {
        jobName: selectedJob,
      });
      toast.success(
        response.data?.message || "Cron job executed. Refresh to view logs."
      );
      await loadLogs();
    } catch (err) {
      const message =
        err.response?.data?.message || "Failed to run cron job manually";
      toast.error(message);
    } finally {
      setInvoking(false);
    }
  };

  useEffect(() => {
    loadLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const jobCards = useMemo(
    () => Object.values(data.jobSummary || {}),
    [data.jobSummary]
  );

  const hasLogs = (data.logs || []).length > 0;

  if (loading) {
    return <LoadingState message="Loading cron logs..." />;
  }

  return (
    <>
      <div className="admin-page-header">
        <h1 className="admin-page-title">Cron Job Logs</h1>
        <p className="admin-page-subtitle">
          Review recent cron executions and their outcomes. Showing up to the
          last {data.totals?.count ?? 0} runs (max {data.totals?.max ?? 500}).
        </p>
      </div>

      <div className="admin-section admin-section--spaced">
        <div className="admin-section-header">
          <div>
            <h3 className="admin-section-title">Job Summary</h3>
            <p className="admin-section-header-text">
              Latest status by job with success and failure counts
            </p>
          </div>
          <div className="admin-actions">
            <button
              className="btn-secondary btn-icon"
              onClick={loadLogs}
              aria-label="Refresh logs"
            >
              <FiRefreshCw />
              <span>Refresh</span>
            </button>
          </div>
        </div>
        {jobCards.length > 0 ? (
          <div className="admin-dashboard-grid">
            {jobCards.map((job) => (
              <div key={job.jobName} className="admin-card">
                <div className="cron-card-header">
                  <h4 className="admin-card-title">
                    {job.jobName.replace(/_/g, " ")}
                  </h4>
                  <span
                    className={
                      STATUS_BADGE_CLASS[job.lastStatus] || "admin-badge"
                    }
                  >
                    {job.lastStatus}
                  </span>
                </div>
                <p className="admin-card-description cron-card-meta">
                  Last run:{" "}
                  {job.lastRunStartedAt
                    ? new Date(job.lastRunStartedAt).toLocaleString()
                    : "—"}
                </p>
                <div className="cron-card-stats">
                  <div>
                    Total runs: <strong>{job.total}</strong>
                  </div>
                  <div>
                    Successful: <strong>{job.success}</strong>
                  </div>
                  <div>
                    Failed: <strong>{job.failed}</strong>
                  </div>
                  <div>
                    Last duration:{" "}
                    <strong>{formatDuration(job.lastDurationMs)}</strong>
                  </div>
                </div>
                {job.lastMessage && (
                  <p className="cron-card-message">{job.lastMessage}</p>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="admin-empty-state">
            <div className="admin-empty-state-icon">
              <FiRefreshCw />
            </div>
            <h3 className="admin-empty-state-title">
              No Cron Activity Tracked
            </h3>
            <p className="admin-empty-state-description">
              Cron jobs have not run yet or logs are unavailable.
            </p>
            <button className="btn-secondary btn-icon" onClick={loadLogs}>
              <FiRefreshCw />
              <span>Refresh</span>
            </button>
          </div>
        )}
      </div>

      <div className="admin-section">
        <div className="admin-section-header admin-section-header--wrap">
          <div>
            <h3 className="admin-section-title">Execution History</h3>
            <p className="admin-section-header-text">
              Detailed view of the most recent cron job executions
            </p>
          </div>
          <div className="cron-controls">
            <div className="cron-runner">
              <label className="cron-runner-label" htmlFor="cron-job-select">
                Manual run
              </label>
              <div className="cron-runner-inputs">
                <select
                  id="cron-job-select"
                  className="form-control cron-runner-select"
                  value={selectedJob}
                  onChange={(event) => setSelectedJob(event.target.value)}
                >
                  {KNOWN_JOBS.map((job) => (
                    <option key={job.value} value={job.value}>
                      {job.label}
                    </option>
                  ))}
                </select>
                <button
                  className="btn-primary btn-icon"
                  onClick={invokeCron}
                  disabled={invoking}
                >
                  <FiPlayCircle />
                  <span>{invoking ? "Running..." : "Run now"}</span>
                </button>
              </div>
            </div>
          </div>
        </div>

        {error && (
          <div className="cron-error-banner" role="alert">
            {error}
          </div>
        )}

        {hasLogs ? (
          <div className="admin-table-wrapper">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Job</th>
                  <th>Status</th>
                  <th>Started</th>
                  <th>Finished</th>
                  <th>Duration</th>
                  <th>Message</th>
                  <th>Metadata</th>
                </tr>
              </thead>
              <tbody>
                {data.logs.map((log) => (
                  <tr key={log.id}>
                    <td className="cron-table-job">
                      {log.job_name.replace(/_/g, " ")}
                    </td>
                    <td>
                      <span
                        className={
                          STATUS_BADGE_CLASS[log.status] || "admin-badge"
                        }
                      >
                        {log.status}
                      </span>
                    </td>
                    <td>
                      {log.started_at
                        ? new Date(log.started_at).toLocaleString()
                        : "-"}
                    </td>
                    <td>
                      {log.finished_at
                        ? new Date(log.finished_at).toLocaleString()
                        : "-"}
                    </td>
                    <td>{formatDuration(log.duration_ms)}</td>
                    <td
                      className="cron-table-message"
                      title={log.message || undefined}
                    >
                      {truncate(log.message || "", 120)}
                    </td>
                    <td>
                      {log.metadata ? (
                        <details className="cron-metadata">
                          <summary>View details</summary>
                          <pre className="admin-metadata-block">
                            {JSON.stringify(log.metadata, null, 2)}
                          </pre>
                        </details>
                      ) : (
                        "-"
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="admin-empty-state">
            <div className="admin-empty-state-icon">
              <FiRefreshCw />
            </div>
            <h3 className="admin-empty-state-title">No Log Entries Yet</h3>
            <p className="admin-empty-state-description">
              Once cron jobs run, their activity will appear here with detailed
              status and metadata.
            </p>
            <button className="btn-secondary btn-icon" onClick={loadLogs}>
              <FiRefreshCw />
              <span>Refresh</span>
            </button>
          </div>
        )}
      </div>
    </>
  );
};

export default CronLogs;
